import { create } from "zustand";
import type { OperatorWithCoords, SearchFilters, SavedSearch } from "../types/database";

export type DrawingShapeType = "polygon" | "rectangle" | "circle";

interface SearchState {
  // Current search state
  filters: SearchFilters;
  results: OperatorWithCoords[];
  selectedOperator: OperatorWithCoords | null;
  hoveredOperatorId: string | null; // ID of operator hovered in list
  isSearching: boolean;
  totalCount: number;

  // Aggregated view state
  totalInView: number;  // Total from grid counts (always available)
  filteredCount: number; // Total filtered operators (always the full filtered count)
  shouldShowAggregated: boolean;  // True when totalInView > 1000

  // Map state
  mapBounds: SearchFilters["bounds"] | null;
  drawnPolygon: GeoJSON.Polygon | null;
  isDrawingMode: boolean;
  isEditingMode: boolean;
  drawingShapeType: DrawingShapeType;
  zoomLevel: number;

  // UI state
  isResultsPanelOpen: boolean;
  viewMode: "map" | "list" | "split";
  listMode: "filtered" | "visible"; // "filtered" = all filtered results, "visible" = only in map view

  // Saved searches
  savedSearches: SavedSearch[];
  loadedSearchId: string | null; // ID of the currently loaded saved search
  loadedSearchSnapshot: {
    polygon: GeoJSON.Polygon | null;
    filters: Partial<SearchFilters>;
  } | null; // Snapshot of original values when loaded

  // Actions
  setFilters: (filters: Partial<SearchFilters>) => void;
  clearFilters: () => void;
  setResults: (results: OperatorWithCoords[], totalCount?: number) => void;
  setSelectedOperator: (operator: OperatorWithCoords | null) => void;
  setHoveredOperatorId: (id: string | null) => void;
  setIsSearching: (searching: boolean) => void;
  setTotalInView: (total: number, shouldShowAggregated: boolean) => void;
  setFilteredCount: (count: number) => void;
  setMapBounds: (bounds: SearchFilters["bounds"] | null) => void;
  setZoomLevel: (zoom: number) => void;
  setDrawnPolygon: (polygon: GeoJSON.Polygon | null) => void;
  setIsDrawingMode: (drawing: boolean, shapeType?: DrawingShapeType) => void;
  setIsEditingMode: (editing: boolean) => void;
  setDrawingShapeType: (shapeType: DrawingShapeType) => void;
  setResultsPanelOpen: (open: boolean) => void;
  setViewMode: (mode: "map" | "list" | "split") => void;
  setListMode: (mode: "filtered" | "visible") => void;
  setSavedSearches: (searches: SavedSearch[]) => void;
  addSavedSearch: (search: SavedSearch) => void;
  removeSavedSearch: (id: string) => void;
  updateSavedSearch: (id: string, updates: Partial<SavedSearch>) => void;
  setLoadedSearchId: (id: string | null) => void;
  setLoadedSearchSnapshot: (snapshot: { polygon: GeoJSON.Polygon | null; filters: Partial<SearchFilters> } | null) => void;
}

const initialFilters: SearchFilters = {
  polygon: undefined,
  bounds: undefined,
  categories: undefined,
  types: undefined,
  region: undefined,
  searchText: undefined,
  selectedZone: undefined,
  dateRange: undefined,
};

export const useSearchStore = create<SearchState>((set) => ({
  // Initial state
  filters: initialFilters,
  results: [],
  selectedOperator: null,
  hoveredOperatorId: null,
  isSearching: false,
  totalCount: 0,
  totalInView: 0,
  filteredCount: 0,
  shouldShowAggregated: true,
  mapBounds: null,
  drawnPolygon: null,
  isDrawingMode: false,
  isEditingMode: false,
  drawingShapeType: "polygon",
  zoomLevel: 6,
  isResultsPanelOpen: true,
  viewMode: "split",
  listMode: "visible",
  savedSearches: [],
  loadedSearchId: null,
  loadedSearchSnapshot: null,

  // Actions
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),

  clearFilters: () =>
    set({
      filters: initialFilters,
      drawnPolygon: null,
      loadedSearchId: null,
      loadedSearchSnapshot: null,
    }),

  setResults: (results, totalCount) =>
    set({
      results,
      totalCount: totalCount ?? results.length,
      isSearching: false,
    }),

  setSelectedOperator: (operator) => set({ selectedOperator: operator }),

  setHoveredOperatorId: (id) => set({ hoveredOperatorId: id }),

  setIsSearching: (searching) => set({ isSearching: searching }),

  setTotalInView: (total, shouldShowAggregated) =>
    set({ totalInView: total, shouldShowAggregated }),

  setFilteredCount: (count) => set({ filteredCount: count }),

  // Sync mapBounds to filters.bounds for server-side filtering
  setMapBounds: (bounds) =>
    set((state) => ({
      mapBounds: bounds,
      filters: { ...state.filters, bounds: bounds || undefined },
    })),

  setZoomLevel: (zoom) => set({ zoomLevel: zoom }),

  setDrawnPolygon: (polygon) =>
    set((state) => ({
      drawnPolygon: polygon,
      filters: { ...state.filters, polygon: polygon || undefined },
    })),

  setIsDrawingMode: (drawing, shapeType) => set((state) => ({
    isDrawingMode: drawing,
    isEditingMode: false,
    drawingShapeType: shapeType || state.drawingShapeType,
  })),

  setIsEditingMode: (editing) => set({ isEditingMode: editing, isDrawingMode: false }),

  setDrawingShapeType: (shapeType) => set({ drawingShapeType: shapeType }),

  setResultsPanelOpen: (open) => set({ isResultsPanelOpen: open }),

  setViewMode: (mode) => set({ viewMode: mode }),

  setListMode: (mode) => set({ listMode: mode }),

  setSavedSearches: (searches) => set({ savedSearches: searches }),

  addSavedSearch: (search) =>
    set((state) => ({
      savedSearches: [search, ...state.savedSearches],
    })),

  removeSavedSearch: (id) =>
    set((state) => ({
      savedSearches: state.savedSearches.filter((s) => s.id !== id),
      // Clear loadedSearchId if the removed search was the loaded one
      loadedSearchId: state.loadedSearchId === id ? null : state.loadedSearchId,
    })),

  updateSavedSearch: (id, updates) =>
    set((state) => ({
      savedSearches: state.savedSearches.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      ),
    })),

  setLoadedSearchId: (id) => set({ loadedSearchId: id }),

  setLoadedSearchSnapshot: (snapshot) => set({ loadedSearchSnapshot: snapshot }),
}));
